import { Virtualizer } from '@tanstack/react-virtual';
import clsx from 'clsx';
import { useCallback, useRef, type JSX } from 'react';

import { DataTableV2Row } from './DataTableV2Row.js';
import { RowFragment } from './DataTableV2RowFragment.js';
import {
  dataTableV2LastLeftPinnedColumnCSS,
  dataTableV2BodyCSS,
  dataTableV2FirstColumnCSS,
  dataTableV2LastColumnCSS,
  dataTableV2FirstRightPinnedColumnCSS,
  dataTableV2FirstUnpinnedColumnCSS,
} from './DataTableV2Rows.sty.js';
import {
  DataTableV2RowContextProvider,
  useDataTableV2,
} from '../contexts/DataTableV2Context.js';
import {
  DETAILS_ROW_IDENTIFIER,
  DETAILS_ROW_PARITY,
  ROW_EXPANDED_ID_SUFFIX,
} from '../features/RowDetails/RowDetails.js';
import { RowDetailsElement } from '../features/RowDetails/RowDetailsElement.js';
import { RowOrderingDragAndDropContext } from '../features/RowOrder/RowOrder.js';
import { SubRowCellContextProvider } from '../features/SubRows/SubRowCellContext.js';
import { type Row } from '../hooks/useTable/types.js';
import type { DataTableV2RowData } from '../public.api.js';
import { DataTableV2Cell } from './DataTableV2Cell.js';

export function DataTableV2Body<TData extends DataTableV2RowData>(
  props: Readonly<{
    rows: Row<TData>[];
    rowVirtualizer: Virtualizer<HTMLDivElement, Element>;
    columnVirtualizer: Virtualizer<HTMLDivElement, Element>;
  }>,
): JSX.Element | null {
  const { rows, rowVirtualizer, columnVirtualizer } = props;
  const table = useDataTableV2();

  // Keep a list of all rows that are currently measured by the virtualizer
  const queuedMeasurementRowIdRef = useRef(new Set());

  const leftPinnedColumns = table.getLeftVisibleLeafColumns();
  const rightPinnedColumns = table.getRightVisibleLeafColumns();

  const rowMeasure = useCallback(
    (node: Element | null) => {
      if (node && node instanceof HTMLElement) {
        const rowId = node.getAttribute('data-rowid');

        // Early exit if the row is not present
        if (rowId === null) {
          return;
        }

        // Measure the row if the measurement is not being queued already
        if (!queuedMeasurementRowIdRef.current.has(rowId)) {
          queuedMeasurementRowIdRef.current.add(rowId);
          // This is necessary to prevent the row heights from being measured too frequently.
          // Especially scrolling too fast with `lineWrap` enabled and changing the table/column width in-between
          // scrolls would cause an infinite loop with the virtualizer (maximum update depth exceeded error).
          requestAnimationFrame(() => {
            rowVirtualizer.measureElement(node);
            // If the measurement is done, remove the row from the measurements set
            queuedMeasurementRowIdRef.current.delete(rowId);
          });
        }
      }
    },
    [rowVirtualizer],
  );

  const columnMeasure = useCallback(
    (node: Element | null) => {
      if (node && node instanceof HTMLElement && node.dataset.columnmeasure) {
        columnVirtualizer.measureElement(node);
      }
    },
    [columnVirtualizer],
  );

  const rowSeparation = table.getVisualVariantOptions().rowSeparation;
  const prefixColumnsCount = table.getBuiltinColumnsCount();
  const virtualColumns = columnVirtualizer.getVirtualItems();

  const cells = rowVirtualizer
    .getVirtualItems()
    .map((virtualRow, rowIndex, allRows) => {
      const originalRow = rows[virtualRow.index];
      let parity: 'even' | 'odd' = virtualRow.index % 2 === 0 ? 'even' : 'odd';
      const draggingRowId = table.getState().draggingRowId;
      const isDraggedRow =
        draggingRowId === undefined
          ? false
          : new RegExp(`^${draggingRowId}(${ROW_EXPANDED_ID_SUFFIX})?$`).test(
              originalRow.id,
            );

      // If the row parity is set in the row, use this one as it
      // could change via row details expansion.
      const detailsParity = originalRow[DETAILS_ROW_PARITY];
      if (detailsParity !== undefined) {
        parity = detailsParity;
      }

      // Need to run this though the symbol accessor instead of the
      // feature, as the feature does not get the information from the
      // extended row model, as the row model runs later.
      if (originalRow[DETAILS_ROW_IDENTIFIER] === true) {
        return (
          <RowDetailsElement
            key={originalRow.id}
            originalRow={originalRow}
            virtualRow={virtualRow}
            rowSeparation={rowSeparation}
            rowParity={parity}
            rowMeasure={rowMeasure}
            isDraggedRow={isDraggedRow}
          >
            {table.options.rowDetailsTemplate?.({
              row: originalRow.original,
            })}
          </RowDetailsElement>
        );
      }

      return (
        <DataTableV2RowContextProvider
          key={`${virtualRow.key}`}
          value={originalRow as Row<unknown>}
        >
          <DataTableV2Row
            row={originalRow}
            rowCount={rows.length}
            virtualRowIndex={virtualRow.index}
            scrollToIndex={rowVirtualizer.scrollToIndex}
            rowMeasure={rowMeasure}
            isDraggedRow={isDraggedRow}
            rowParity={parity}
          >
            {table.options.enableRowOrderDragAndDrop === false ? (
              virtualColumns.map((virtualCell, cellIndex, allCells) => {
                // NOTE: for row ordering this code was duplicated in DataTableV2RowFragment
                // so all changes here have to be applied there as well
                // TODO: remove duplication when refactoring for subgrids (APPDEV-12251)
                const visibleCells = originalRow.getVisibleCells();
                const cell = visibleCells[virtualCell.index];

                return (
                  <SubRowCellContextProvider
                    key={`${virtualRow.key}-${virtualCell.key}`}
                    isSubRowIndicatorColumn={
                      table.options.enableExpanding === true &&
                      table.getSubRowIndicatorColumnId() === cell.column.id
                    }
                    cell={cell}
                  >
                    <DataTableV2Cell
                      cell={cell}
                      columnMeasure={columnMeasure}
                      // For columns, only measure the first rendered row.
                      // All other rows will have the same column widths.
                      shouldMeasureColumn={
                        allRows.at(0)?.index === virtualRow.index
                      }
                      rowIndex={virtualRow.index}
                      columnIndex={virtualCell.index}
                      style={cell.getGridPosition(
                        `${virtualCell.index}/${virtualRow.index}`,
                      )}
                      className={clsx({
                        [dataTableV2LastLeftPinnedColumnCSS]:
                          leftPinnedColumns.at(-1)?.id === cell.column?.id,
                        [dataTableV2FirstRightPinnedColumnCSS]:
                          rightPinnedColumns.at(0)?.id === cell.column?.id,
                        [dataTableV2FirstColumnCSS]: virtualCell.index === 0,
                        [dataTableV2FirstUnpinnedColumnCSS]:
                          prefixColumnsCount > 0 &&
                          virtualCell.index === prefixColumnsCount,
                        [dataTableV2LastColumnCSS]:
                          virtualCell.index === visibleCells.length - 1,
                        dataTableV2BodyCSS,
                      })}
                      cellDeps={cell.column.columnDef?.meta
                        ?.cellDeps?.(cell)
                        .toString()}
                    />
                  </SubRowCellContextProvider>
                );
              })
            ) : (
              <RowFragment
                key={`row-fragment-${virtualRow.key}`}
                virtualRow={virtualRow}
                rows={rows}
                columnMeasure={columnMeasure}
                isInDragOverlay={false}
                virtualColumns={virtualColumns}
                subRowIndicatorColumnId={table.getSubRowIndicatorColumnId()}
                enableExpanding={table.options.enableExpanding}
              />
            )}
          </DataTableV2Row>
        </DataTableV2RowContextProvider>
      );
    });

  const bodyContent = table.options.enableRowOrderDragAndDrop ? (
    <RowOrderingDragAndDropContext<TData>
      rows={rows}
      virtualColumns={virtualColumns}
      rowSeparation={rowSeparation}
    >
      {cells}
    </RowOrderingDragAndDropContext>
  ) : (
    // If row order drag and drop is not enabled then don't use the RowOrderingDragAndDropContext
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <>{cells}</>
  );

  return (
    <div
      role="rowgroup"
      data-rowgroup="body"
      style={{
        display: 'grid',
        gridTemplateColumns: 'subgrid',
        gridColumnStart: 1,
        gridColumnEnd: 'row-end',
        gridTemplateRows: 'subgrid',
        gridRowStart: 'body-start',
        gridRowEnd: 'body-end',
      }}
    >
      {bodyContent}
    </div>
  );
}

DataTableV2Body.displayName = 'DataTableV2Body';
